import React, {useState} from 'react';
import {Send, Users, CheckCircle} from 'lucide-react';
import preparatoryApiService from "../../api/preparatoryApiService";

/**
 * Preparatory stage peer assessment component with modern UI design
 * Matches the style of LessonPlanHistory and AssessmentManagementPanel
 */
const PreparatoryPeerAssessment = ({
                                       assignmentId,
                                       submission,
                                       onSubmitComplete
                                   }) => {
    // Initialize state for form data
    const [peerAssessmentForm, setPeerAssessmentForm] = useState({
        can_express_emotions: "Not sure",
        can_calm_down: "Not sure",
        can_understand_others: "Not sure",
        respects_opinions: "Not sure",
        helps_after_fight: "Not sure",
        comforts_when_sad: "Not sure",
        name: "",
        friend_name: ""
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Define questions for better organization
    const questions = [
        { key: 'can_express_emotions', text: 'My friend can talk about how he/she feels, e.g., happy, confident, upset, or angry.' },
        { key: 'can_calm_down', text: 'My friend can calm himself/herself down during difficult situations.' },
        { key: 'can_understand_others', text: 'My friend can understand how his/her friends feel.' },
        { key: 'respects_opinions', text: 'My friend respects everyone\'s opinions.' },
        { key: 'helps_after_fight', text: 'My friend can help others make up after a fight.' },
        { key: 'comforts_when_sad', text: 'When someone is sad, my friend can make them feel better.' }
    ];

    // Handle input changes
    const handleInputChange = (e) => {
        setPeerAssessmentForm(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    // Map UI values to API values
    const mapValueToResponse = (value) => {
        if (value === true) return "Yes";
        if (value === false) return "No";
        if (value === "sometimes") return "Sometimes";
        return "Not sure"; // Default/null case
    };

    // Handle changes to radio button fields
    const handlePeerAssessmentChange = (field, value) => {
        // Store the UI value in state
        setPeerAssessmentForm(prev => ({
            ...prev,
            [field]: mapValueToResponse(value)
        }));
    };

    // Submit peer assessment
    const handlePeerAssessmentSubmit = async () => {
        try {
            setIsSubmitting(true);
            setError(null);

            // Extract data for the API using the correct field names
            const apiData = {
                name: peerAssessmentForm.name,
                friend_name: peerAssessmentForm.friend_name,
                can_express_emotions: peerAssessmentForm.can_express_emotions,
                can_calm_down: peerAssessmentForm.can_calm_down,
                can_understand_others: peerAssessmentForm.can_understand_others,
                respects_opinions: peerAssessmentForm.respects_opinions,
                helps_after_fight: peerAssessmentForm.helps_after_fight,
                comforts_when_sad: peerAssessmentForm.comforts_when_sad
            };

            const response = await preparatoryApiService.addPeerAssessment(assignmentId, apiData);

            if (onSubmitComplete) {
                onSubmitComplete(response);
            }
        } catch (err) {
            // Handle detailed validation errors
            if (typeof err.message === 'string' && err.message.includes('[object Object]')) {
                setError("Validation error: Please fill in all required fields.");
            } else {
                setError(err.message || "Failed to submit peer assessment");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Get response styling
    const getResponseStyling = (currentResponse, targetResponse) => {
        if (currentResponse === targetResponse) {
            switch (targetResponse) {
                case "Yes":
                    return 'bg-green-100 border-2 border-green-500 scale-110';
                case "Sometimes":
                    return 'bg-yellow-100 border-2 border-yellow-500 scale-110';
                case "No":
                    return 'bg-red-100 border-2 border-red-500 scale-110';
                case "Not sure":
                    return 'bg-gray-200 border-2 border-gray-500 scale-110';
                default:
                    return 'bg-gray-100';
            }
        }
        return 'bg-gray-100';
    };

    // Helper function to determine which response was selected
    const isSelected = (field, value) => {
        const response = submission.peer_assessment[field];
        return response === value;
    };

    // If already submitted, show the submitted assessment
    if (submission && submission.peer_assessment) {
        return (
            <div className="bg-white font-[poppins] rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
                {/* Header */}
                <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-pink-50">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#D67C2C] shadow-lg">
                            <Users className="w-6 h-6 text-white"/>
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Peer Assessment Complete</h2>
                            <p className="text-gray-600 mt-1">Your friend's feedback has been submitted</p>
                        </div>
                    </div>
                </div>

                <div className="p-8">
                    <div className="bg-purple-50 rounded-xl p-4 mb-8 border border-purple-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-blue-100 rounded-xl p-4 border border-blue-200">
                                <p className="text-sm font-medium text-blue-800">My name:</p>
                                <p className="text-lg font-semibold text-blue-900 mt-1">{submission.peer_assessment.name || "Not provided"}</p>
                            </div>
                            <div className="bg-green-100 rounded-xl p-4 border border-green-200">
                                <p className="text-sm font-medium text-green-800">My friend's name:</p>
                                <p className="text-lg font-semibold text-green-900 mt-1">{submission.peer_assessment.friend_name || "Not provided"}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Statement</th>
                                    <th className="px-6 py-4 text-center text-sm font-medium text-gray-900">Response</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {questions.map((question, index) => (
                                    <tr key={question.key} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm text-gray-900">
                                            {index + 1}. {question.text}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex justify-center">
                                                <div className="flex items-center gap-3">
                                                    <div className="flex flex-col items-center">
                                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg transition-all ${getResponseStyling(submission.peer_assessment[question.key], "Yes")}`}>
                                                            <span className="text-xl">😊👍</span>
                                                        </div>
                                                        <span className="mt-1 text-xs font-medium text-gray-700">Yes</span>
                                                    </div>

                                                    <div className="flex flex-col items-center">
                                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg transition-all ${getResponseStyling(submission.peer_assessment[question.key], "Sometimes")}`}>
                                                            <span className="text-xl">😐👈</span>
                                                        </div>
                                                        <span className="mt-1 text-xs font-medium text-gray-700">Sometimes</span>
                                                    </div>

                                                    <div className="flex flex-col items-center">
                                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg transition-all ${getResponseStyling(submission.peer_assessment[question.key], "No")}`}>
                                                            <span className="text-xl">😔👎</span>
                                                        </div>
                                                        <span className="mt-1 text-xs font-medium text-gray-700">No</span>
                                                    </div>

                                                    <div className="flex flex-col items-center">
                                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg transition-all ${getResponseStyling(submission.peer_assessment[question.key], "Not sure")}`}>
                                                            <span className="text-xl">🤔</span>
                                                        </div>
                                                        <span className="mt-1 text-xs font-medium text-gray-700">Not sure</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    // Otherwise, show the assessment form
    return (
        <div className="bg-white font-[poppins] rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
            {/* Header */}
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-pink-50">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#D67C2C] shadow-lg">
                        <Users className="w-6 h-6 text-white"/>
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Peer Assessment</h2>
                        <p className="text-gray-600 mt-1">Circle the most appropriate option for each sentence</p>
                    </div>
                </div>
            </div>

            <div className="p-8">
                <div className="bg-purple-50 rounded-xl p-4 mb-8 border border-purple-200">
                    <p className="text-sm font-medium text-purple-800 mb-4">Instructions</p>
                    <p className="text-sm text-purple-700 mb-4">
                        Please fill in your names and then evaluate your friend based on the statements below.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-blue-100 rounded-xl p-4 border border-blue-200">
                            <label className="block text-sm font-medium text-blue-800 mb-2">My name:</label>
                            <input
                                type="text"
                                name="name"
                                value={peerAssessmentForm.name}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Enter your name"
                            />
                        </div>
                        <div className="bg-green-100 rounded-xl p-4 border border-green-200">
                            <label className="block text-sm font-medium text-green-800 mb-2">My friend's name:</label>
                            <input
                                type="text"
                                name="friend_name"
                                value={peerAssessmentForm.friend_name}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="Enter your friend's name"
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Statement</th>
                                <th className="px-6 py-4 text-center text-sm font-medium text-gray-900">Your Response</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {questions.map((question, index) => (
                                <tr key={question.key} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 text-sm text-gray-900">
                                        {index + 1}. {question.text}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex justify-center">
                                            <div className="flex items-center gap-3">
                                                <div className="flex flex-col items-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => handlePeerAssessmentChange(question.key, true)}
                                                        className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg transition-all hover:scale-105 ${peerAssessmentForm[question.key] === "Yes" ? 'bg-green-100 border-2 border-green-500 scale-110' : 'bg-gray-100 hover:bg-green-50'}`}
                                                    >
                                                        <span className="text-xl">😊👍</span>
                                                    </button>
                                                    <span className="mt-1 text-xs font-medium text-gray-700">Yes</span>
                                                </div>

                                                <div className="flex flex-col items-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => handlePeerAssessmentChange(question.key, 'sometimes')}
                                                        className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg transition-all hover:scale-105 ${peerAssessmentForm[question.key] === "Sometimes" ? 'bg-yellow-100 border-2 border-yellow-500 scale-110' : 'bg-gray-100 hover:bg-yellow-50'}`}
                                                    >
                                                        <span className="text-xl">😐👈</span>
                                                    </button>
                                                    <span className="mt-1 text-xs font-medium text-gray-700">Sometimes</span>
                                                </div>

                                                <div className="flex flex-col items-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => handlePeerAssessmentChange(question.key, false)}
                                                        className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg transition-all hover:scale-105 ${peerAssessmentForm[question.key] === "No" ? 'bg-red-100 border-2 border-red-500 scale-110' : 'bg-gray-100 hover:bg-red-50'}`}
                                                    >
                                                        <span className="text-xl">😔👎</span>
                                                    </button>
                                                    <span className="mt-1 text-xs font-medium text-gray-700">No</span>
                                                </div>

                                                <div className="flex flex-col items-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => handlePeerAssessmentChange(question.key, null)}
                                                        className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg transition-all hover:scale-105 ${peerAssessmentForm[question.key] === "Not sure" ? 'bg-gray-200 border-2 border-gray-500 scale-110' : 'bg-gray-100 hover:bg-gray-200'}`}
                                                    >
                                                        <span className="text-xl">🤔</span>
                                                    </button>
                                                    <span className="mt-1 text-xs font-medium text-gray-700">Not sure</span>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Error message */}
                {error && (
                    <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <CheckCircle className="h-5 w-5 text-red-400" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-800">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Submit button */}
                <div className="flex justify-center mt-8 pt-6 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={handlePeerAssessmentSubmit}
                        disabled={isSubmitting}
                        className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-xl shadow-lg text-white bg-[#D67C2C] hover:bg-[#c06a25] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D67C2C] transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Saving...
                            </>
                        ) : (
                            <>
                                <Send size={20} className="mr-3"/>
                                Submit Peer Assessment
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PreparatoryPeerAssessment;